import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import AuthLayout from '../../components/layout/AuthLayout';
import Spinner from '../../components/common/Spinner';
import Field from '../../components/common/Field';
import CountryCodePicker from './CountryCodePicker';
import authService from '../../api/services/auth.service';
import useAuthStore from '../../store/useAuthStore';
import { showToast } from '../../store/useToastStore';
import { buildUsername, isValidPhone, formatPhoneUS } from '../../utils/phone';
import { DEFAULT_COUNTRY } from '../../utils/countries';
import { RECAPTCHA_SITE_KEY } from '../../utils/constants';
import { PATHS } from '../../routes/navConfig';

export default function PhoneLoginPage() {
  const setLoginIdentity = useAuthStore((s) => s.setLoginIdentity);
  const markOtpSent = useAuthStore((s) => s.markOtpSent);
  const [value, setValue] = useState('');
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [sending, setSending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  /* react-google-recaptcha never resets its widget on unmount, so repeated
     login/logout cycles leave orphaned grecaptcha clients whose timers later
     throw (e.g. "reading 'M_ID'"). Reset before the page unmounts. */
  useEffect(() => {
    const widget = recaptchaRef.current;
    return () => {
      try {
        widget?.reset();
      } catch {
        /* widget already torn down */
      }
    };
  }, []);

  const sendCode = async () => {
    if (!captchaToken) return;
    if (!isValidPhone(country.dial, value)) {
      window.alert('Enter a valid phone number.');
      return;
    }
    const username = buildUsername(country.dial, value);
    setSending(true);
    try {
      await authService.sendOtp(username, captchaToken);
      setLoginIdentity(`+${country.dial} ${value.trim()}`, username);
      markOtpSent();
      navigate(PATHS.VERIFY);
    } catch (err) {
      showToast(err.message);
      /* reCAPTCHA tokens are single-use — the widget must be solved again. */
      try {
        recaptchaRef.current?.reset();
      } catch {
        /* stale grecaptcha widget — the token state below still clears */
      }
      setCaptchaToken(null);
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthLayout>
      <h1>Sign in to your portal</h1>
      <p className="sub">Enter the phone number on file for your class.</p>
      <div className="form-stack">
        <Field label="Phone Number">
          <div style={{ display: 'flex', gap: 8 }}>
            <CountryCodePicker value={country} onChange={setCountry} />
            <input
              type="tel"
              value={value}
              placeholder="(512) 555-0147"
              style={{ flex: 1 }}
              onChange={(e) => setValue(country.dial === '1' ? formatPhoneUS(e.target.value) : e.target.value)}
            />
          </div>
        </Field>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={setCaptchaToken}
            onExpired={() => setCaptchaToken(null)}
            onErrored={() => setCaptchaToken(null)}
          />
        </div>
        <button
          className="btn btn-primary btn-primary-full"
          disabled={sending || !captchaToken}
          onClick={sendCode}
        >
          {sending ? <><Spinner /> Sending…</> : 'Send Code'}
        </button>
      </div>
    </AuthLayout>
  );
}
